import { useLocalStorage } from '@uidotdev/usehooks'
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs'
import { useCallback, useMemo, useState } from 'react'
import { AddFindingsDrawer } from '../../components/drawers/add-findings-drawer.tsx'
import { RepositorySettingsDrawer } from '../../components/drawers/repository-settings-drawer'

import { WorkflowDrawer, type DrawerContent } from '../../components/drawers/workflow-drawer'
import { OnboardingDialog } from '../../components/onboarding-dialog.tsx'
import { RepositoriesTable, type FilteredEmptyState } from '../../components/tables/repositories-table.tsx'
import { usePagination } from '../../components/tables/use-pagination.ts'
import { useRepositoryRiskScores } from '../../hooks/use-repository-risk-scores.ts'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import {
  UNGROUPED_PROJECT_FILTER,
  useGetRepositories,
  useGetRepository,
  usePostUploadScan,
  usePrefetchRepositoriesPageIntegrations,
} from '../../utils/api-client/api-hooks.ts'
import type { Repository, Tool } from '../../utils/api-client/api-schemas.ts'
import { getEnvironmentData } from '../../utils/hooks/get-environment-data.ts'
import { getToolName } from '../../utils/repository-utils.tsx'
import { getRuntimeEnv } from '../../utils/runtime-env.ts'
import { AddRepositoryModal } from '../repositories-page/add-repository-modal.tsx'
import { RepositoriesControls } from './repositories-controls.tsx'
import * as styles from './repositories-page.css.ts'
import {
  createBandFilter,
  createRiskComparator,
  createScoredFilter,
  DEFAULT_REPOSITORY_QUICK_VIEW_ID,
  hasEnoughScoredRepositories,
  parseQuickView,
} from './repository-quick-views.ts'
import { RepositoryRiskDrawer } from './repository-risk-drawer.tsx'
import { createRiskColumnDefinitions } from './repository-risk.columns.tsx'
import { type RiskBandId } from './repository-risk.ts'
import { RiskReferenceDrawer } from './risk-reference-drawer.tsx'

const NO_REPOSITORIES: Repository[] = []

export function RepositoriesPage() {
  // Warm the integrations queries this page's own surfaces read — the new-repository modal's first
  // page and the drawers' tool counts — so neither shows a loading state when opened. Prefetch
  // populates the cache without subscribing to the query.
  usePrefetchRepositoriesPageIntegrations()

  const { pagination, onPaginationChange } = usePagination({
    defaultPageIndex: 0,
    defaultPageSize: 50,
  })
  const [searchTerm, setSearchTerm] = useQueryState('search', {
    defaultValue: '',
    history: 'push',
  })
  const [rawView, setRawView] = useQueryState('view', {
    defaultValue: DEFAULT_REPOSITORY_QUICK_VIEW_ID,
    history: 'push',
  })
  // Server-side, unlike the band picker: membership is a property the API filters on before paginating,
  // so a project filter narrows the estate rather than the fetched page. In the URL like the search, so a
  // filtered list can be shared. A list because the filter is a checkbox group and the API combines
  // repeated `project` values with OR, so several may be selected at once.
  const [selectedProjectIds, setSelectedProjectIds] = useQueryState(
    'project',
    parseAsArrayOf(parseAsString).withDefault([]).withOptions({ history: 'push' })
  )
  const { data: repositories } = useGetRepositories({
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    searchTerm,
    projectIds: selectedProjectIds,
  })
  const fetchedRepositories = useMemo(() => repositories?._embedded.items ?? [], [repositories])
  const { isLabsEnabled } = getRuntimeEnv()

  /**
   * A score per repository, derived from each one's threat-model scorecard.
   *
   * Fanned out over the fetched page rather than the estate, which is also the honest scope of the
   * ranking this feeds: until the API can order before paginating, "ranked by risk" ranks what was
   * fetched. ENH-570 carries the rest.
   */
  const riskScores = useRepositoryRiskScores(isLabsEnabled ? fetchedRepositories : NO_REPOSITORIES)

  /**
   * Every score is `loading` until its request settles, and a loading score is not a scored one, so this
   * is `false` while the fan-out is in flight and after a failure. Deliberate: the risk UI appears once
   * rather than flashing in, and a failed request does not leave a column nothing can populate.
   *
   * The Labs check is deliberate, not redundant: with no scores the threshold hides the risk UI too,
   * but only because `MINIMUM_SCORED_REPOSITORIES` is above zero.
   */
  const isRiskAvailable = useMemo(
    () => isLabsEnabled && hasEnoughScoredRepositories(riskScores),
    [isLabsEnabled, riskScores]
  )
  // Parsed rather than trusted: `?view=` is user-editable, and an unrecognised value should land on the
  // default list rather than on an empty screen. Below the threshold the ranked view is not on offer at
  // all, so a saved link to it lands on the list too — the param is left alone rather than rewritten.
  const quickView = isRiskAvailable ? parseQuickView(rawView) : 'all-repositories'
  const [selectedBands, setSelectedBands] = useState<RiskBandId[]>([])
  // Off by default: the ranked view is a ranking, and a repository with no score has no place in one.
  const [isShowingUnranked, setIsShowingUnranked] = useState(false)

  const [selectedRiskRepositoryId, setSelectedRiskRepositoryId] = useState<string>()

  const [isAboutRiskOpen, setIsAboutRiskOpen] = useState(false)
  const openAboutRisk = useCallback(() => setIsAboutRiskOpen(true), [])
  const closeAboutRisk = useCallback(() => setIsAboutRiskOpen(false), [])

  const riskColumns = useMemo(
    () =>
      createRiskColumnDefinitions({
        onSelectRepository: setSelectedRiskRepositoryId,
        onOpenAboutRisk: openAboutRisk,
      }),
    [openAboutRisk]
  )
  const isRankedByRisk = quickView === 'ranked-by-risk'
  // The quick view carries the ordering, and — unlike the band picker — a filter of its own.
  const rowComparator = useMemo(
    () => (isRankedByRisk ? createRiskComparator(riskScores) : undefined),
    [isRankedByRisk, riskScores]
  )
  const rowFilter = useMemo(() => {
    const bandFilter = createBandFilter({ scores: riskScores, bands: selectedBands })
    if (!isRankedByRisk || isShowingUnranked) {
      return bandFilter
    }
    // Only rows with a score can take a position in the order. "Show unranked" opts them back in, at
    // which point `createRiskComparator` sorts them last — unknown is not the same as safe.
    const scoredFilter = createScoredFilter(riskScores)
    return (repository: Repository) => scoredFilter(repository) && bandFilter(repository)
  }, [riskScores, selectedBands, isRankedByRisk, isShowingUnranked])

  const toggleBand = useCallback(
    (band: RiskBandId) =>
      setSelectedBands(current =>
        current.includes(band) ? current.filter(selected => selected !== band) : [...current, band]
      ),
    []
  )
  const resetBands = useCallback(() => setSelectedBands([]), [])
  const toggleShowUnranked = useCallback((isShowing: boolean) => setIsShowingUnranked(isShowing), [])

  const toggleProject = useCallback(
    (projectId: string) => {
      // A different membership filter makes the current page number meaningless, as a search change does.
      onPaginationChange({ pageIndex: 0, pageSize: pagination.pageSize })
      void setSelectedProjectIds(current =>
        current.includes(projectId) ? current.filter(id => id !== projectId) : [...current, projectId]
      )
    },
    [onPaginationChange, pagination.pageSize, setSelectedProjectIds]
  )
  const resetProjects = useCallback(() => {
    onPaginationChange({ pageIndex: 0, pageSize: pagination.pageSize })
    void setSelectedProjectIds([])
  }, [onPaginationChange, pagination.pageSize, setSelectedProjectIds])

  const changeQuickView = useCallback(
    (next: string) => {
      // Same reset as a search change: a different ordering makes the current page number meaningless.
      if (next !== quickView) {
        onPaginationChange({ pageIndex: 0, pageSize: pagination.pageSize })
      }
      void setRawView(next)
    },
    [quickView, setRawView, onPaginationChange, pagination.pageSize]
  )

  /**
   * The rows the table renders, ordered and filtered once, here.
   *
   * Derived in one place because the count above the table and the rows inside it have to be the same
   * set. They used to be two filters over the same array — this one for the count, another inside the
   * table for the rows — which is a single derivation written twice and free to drift apart.
   */
  const visibleRepositories = useMemo(() => {
    const kept = fetchedRepositories.filter(rowFilter)
    return rowComparator ? [...kept].sort(rowComparator) : kept
  }, [fetchedRepositories, rowFilter, rowComparator])

  /**
   * The API's total is the honest count until something is filtered out, at which point it describes the
   * whole estate while the table shows a filtered page — so the filtered case counts what is actually
   * visible. The ranked view filters too unless "Show unranked" is on, not just the band picker.
   *
   * Both counts are approximations only while the filtering runs client-side over one fetched page.
   * ENH-570 moves ordering and filtering to the API, after which `total` is the filtered total and this
   * whole special-case goes away.
   */
  const isFiltered = selectedBands.length > 0 || (isRankedByRisk && !isShowingUnranked)
  const visibleCount = isFiltered ? visibleRepositories.length : repositories?.total

  // What an empty page should say: the client-side filters emptied a page that had rows, or the project
  // filter matched nothing on the server. Several selections get their own wording, since naming one of
  // them would be picking arbitrarily among the reasons the page is empty.
  const filteredEmptyState: FilteredEmptyState | undefined =
    isFiltered && fetchedRepositories.length > 0
      ? 'filters'
      : selectedProjectIds.length > 1
        ? 'projects'
        : selectedProjectIds.includes(UNGROUPED_PROJECT_FILTER)
          ? 'ungrouped'
          : selectedProjectIds.length > 0
            ? 'project'
            : undefined

  const environmentData = getEnvironmentData()

  const [selectedWorkflowRepositoryId, setSelectedWorkflowRepositoryId] = useState<string>()

  const [selectedAddFindingsRepositoryId, setSelectedAddFindingsRepositoryId] = useState<string>()

  const [isOnboardingDialogOpen, setIsOnboardingDialogOpen] = useLocalStorage('isOnboardingDialogOpen', true)

  const [addRepositoryDialogOpen, setAddRepositoryDialogOpen] = useState(false)
  const handleOpenAddRepository = useCallback(() => setAddRepositoryDialogOpen(true), [])
  const handleCloseAddRepository = useCallback(() => setAddRepositoryDialogOpen(false), [])
  const handleAddFindings = useCallback((repositoryId: string) => setSelectedAddFindingsRepositoryId(repositoryId), [])

  const handleSyncTool = useHandleSyncTool()

  const [workflowDrawerContent, setWorkflowDrawerContent] = useState<DrawerContent>('create')

  const filterRepositories = useCallback(
    (value: string) => {
      // Only reset pagination if search term actually changed
      if (value !== searchTerm) {
        onPaginationChange({ pageIndex: 0, pageSize: pagination.pageSize })
      }
      setSearchTerm(value)
    },
    [searchTerm, setSearchTerm, onPaginationChange, pagination.pageSize]
  )

  const [selectedSettingsRepositoryId, setSelectedSettingsRepositoryId] = useState<string | undefined>()

  const { data: selectedSettingsRepository } = useGetRepository({ repositoryId: selectedSettingsRepositoryId })

  return (
    <div className={styles.mainContainer}>
      <div className={styles.topLevelContainer}>
        <OnboardingDialog isOpen={isOnboardingDialogOpen} setIsOpen={setIsOnboardingDialogOpen} />
        {selectedAddFindingsRepositoryId && (
          <AddFindingsDrawer
            repositoryId={selectedAddFindingsRepositoryId}
            onCloseDrawer={() => setSelectedAddFindingsRepositoryId(undefined)}
          />
        )}
        {selectedWorkflowRepositoryId && (
          <WorkflowDrawer
            repositoryId={selectedWorkflowRepositoryId}
            repositoryDisplayName={
              repositories?._embedded.items.find(repo => repo.id === selectedWorkflowRepositoryId)?.full_name ?? ''
            }
            repositoryType={
              repositories?._embedded.items.find(repo => repo.id === selectedWorkflowRepositoryId)?.type ?? 'git'
            }
            onCloseDrawer={() => {
              setSelectedWorkflowRepositoryId(undefined)
              setWorkflowDrawerContent('create')
            }}
            drawerContent={workflowDrawerContent}
            onSetDrawerContent={setWorkflowDrawerContent}
          />
        )}
        {addRepositoryDialogOpen && <AddRepositoryModal onClose={handleCloseAddRepository} />}
        {selectedSettingsRepository && (
          <RepositorySettingsDrawer
            repository={selectedSettingsRepository}
            onCloseDrawer={() => setSelectedSettingsRepositoryId(undefined)}
            onUploadClick={() => setSelectedAddFindingsRepositoryId(selectedSettingsRepository.id)}
          />
        )}

        {selectedRiskRepositoryId && (
          <RepositoryRiskDrawer
            repositoryId={selectedRiskRepositoryId}
            onCloseDrawer={() => setSelectedRiskRepositoryId(undefined)}
          />
        )}

        {isAboutRiskOpen && <RiskReferenceDrawer onClose={closeAboutRisk} />}

        <RepositoriesControls
          count={visibleCount}
          description={`Connected to ${environmentData.productName} for analysis`}
          quickView={quickView}
          onChangeQuickView={changeQuickView}
          isRiskAvailable={isRiskAvailable}
          selectedBands={selectedBands}
          onToggleBand={toggleBand}
          onResetBands={resetBands}
          selectedProjectIds={selectedProjectIds}
          onToggleProject={toggleProject}
          onResetProjects={resetProjects}
          isShowingUnranked={isShowingUnranked}
          onToggleShowUnranked={toggleShowUnranked}
          searchTerm={searchTerm}
          onChangeSearchTerm={filterRepositories}
          onOpenAddRepository={handleOpenAddRepository}
          hasAzureRepositories={fetchedRepositories.some(repository => repository.type === 'azure')}
        />

        <RepositoriesTable
          columnDefinitionOptions={{
            setSelectedWorkflowRepositoryId,
            setSelectedSettingsRepositoryId,
            setWorkflowDrawerContent,
            onAddFindings: handleAddFindings,
            onSyncTool: handleSyncTool,
          }}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          onOpenAddRepository={handleOpenAddRepository}
          searchTerm={searchTerm}
          projectIds={selectedProjectIds}
          filteredEmptyState={filteredEmptyState}
          extraColumns={isRiskAvailable ? riskColumns : undefined}
          rows={visibleRepositories}
        />
      </div>
    </div>
  )
}

const useHandleSyncTool = () => {
  const { addToast } = useToastService()
  const showSyncError = useShowMutationError<typeof usePostUploadScan>({
    prefix: (_error, variables) => `Failed to sync ${getToolName(variables.body.metadata.tool)} findings`,
  })
  const { mutate } = usePostUploadScan()

  return useCallback(
    (repositoryId: string, tool: Tool, repositoryName: string) => {
      mutate(
        { repositoryId, body: { metadata: { tool, workflow_execution_policy: 'execute' } } },
        {
          onSuccess: () => {
            addToast({
              message: (
                <>
                  Latest {getToolName(tool)} findings synced to {repositoryName}
                </>
              ),
              variant: 'success',
            })
          },
          onError: showSyncError,
        }
      )
    },
    [mutate, addToast, showSyncError]
  )
}
